import type { CalendarQuery } from '@complan/contracts';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  clearDimension,
  removeListValue,
  type BooleanFilterKey,
  type ListFilterKey,
} from '@/shared/url/calendar-search-params';
import { deriveChips, type ChipRemoval, type LabelResolver } from './filter-chips';

/**
 * The filter-chip row (UI spec §3.7).
 *
 * Chips sit below the filter bar: `--accent-soft` background, `#CFE2F5` border,
 * `--accent-hover` text, `--radius-chip` (12px) corners. Each active filter is a
 * removable chip; the row **terminates** with the mandatory result-context chip
 * (`N initiatives visible`), which is how R11.9's result count surfaces visually —
 * it is not optional decoration, so it is always rendered, the zero count
 * included.
 *
 * The chips are derived purely from the URL-held query (`deriveChips`), so they
 * describe exactly the single source of truth and nothing else. A chip's remove
 * control mutates that same URL through the codec helpers, so removing a chip is
 * clearing the dimension (or the one value) it stands for — the calendar, count
 * and export all follow from the URL change.
 */

export interface FilterChipsProps {
  readonly query: CalendarQuery;
  readonly count: number;
  readonly search: URLSearchParams;
  readonly onSearchChange: (next: URLSearchParams) => void;
  /** Resolves an identifier to its display name; a chip falls back to the raw id. */
  readonly resolveLabel?: LabelResolver;
}

export function FilterChips({
  query,
  count,
  search,
  onSearchChange,
  resolveLabel,
}: FilterChipsProps): React.JSX.Element {
  const { t } = useTranslation();
  const chips = deriveChips(query, count, resolveLabel, t);

  const remove = (removal: ChipRemoval): void => {
    if (removal.type === 'search') {
      onSearchChange(clearDimension(search, 'search'));
    } else if (removal.type === 'boolean') {
      onSearchChange(clearDimension(search, removal.key as BooleanFilterKey));
    } else {
      onSearchChange(removeListValue(search, removal.key as ListFilterKey, removal.value));
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-[6px]"
      // R11.9: the count is announced on every apply, change or clear.
      aria-live="polite"
    >
      {chips.map((chip) =>
        chip.kind === 'result' ? (
          <span
            key={chip.key}
            data-chip="result"
            className="border-line bg-surface-alt text-muted-foreground inline-flex items-center rounded-[var(--radius-chip)] border px-[10px] py-[3px] text-[11px] font-semibold"
          >
            {chip.label}
          </span>
        ) : (
          <span
            key={chip.key}
            data-chip="filter"
            className={cn(
              'inline-flex items-center gap-1 rounded-[var(--radius-chip)] border px-[10px] py-[3px] text-[11px]',
              'border-[#CFE2F5] bg-accent-soft text-accent-hover',
            )}
          >
            {chip.label}
            {chip.removal ? (
              <button
                type="button"
                aria-label={t('filter.removeChip', { label: chip.label })}
                className="hover:text-accent-line -mr-1 inline-flex size-[14px] items-center justify-center rounded-full"
                onClick={() => chip.removal && remove(chip.removal)}
              >
                <XIcon className="size-3" aria-hidden="true" />
              </button>
            ) : null}
          </span>
        ),
      )}
    </div>
  );
}
